import { ImageProps } from 'next/image'

import { Recipe, Ingredient, RecipeStep } from '../../types/fam-graphql'
import { IconName, Icons } from '../../components/Icon'
import { ActionButtonProps } from '../../components/ActionButton'
import { colors } from '../../theme'
import { dotToComma } from '../../utils/FormatUtils'
import { VideoProps } from '../../components/Video'
import {
  unitsTransformer,
  weightStepTransformer,
} from '../../utils/RecipeUtils'
import { wysiwygTransformer } from '../../utils/DomUtils'
import APP from '../../configuration/app'
import FamilyStep from '../../components/Family/RecipeStep'
import { StepProps } from '../../components/StepCard'
import {
  getCardBackgroundColorByFamilyProfile,
  getRecipeChildProfile,
  getSecondaryBackgroundImageByFamilyProfile,
  getTagBackgroundColorByFamilyProfile,
  getTagTextColorByFamilyProfile,
} from '../Family/FamilyHelpers'
import { FamilyProfile } from '../../types'
import Router from '../../routes/router'
import routes from '../../routes/definitions'
import { api } from '../../configuration'

const stepsIngredientsTransformer = (
  stepsIngredients: RecipeStep['ingredients']
) => {
  return stepsIngredients?.map((ingredient: Ingredient) => {
    const quantity = ingredient?.ingredient_recipe_step?.quantity ?? 0

    return {
      name:
        quantity >= 2
          ? ingredient?.plural ?? ingredient?.name
          : ingredient?.name,
      units: dotToComma(
        unitsTransformer(ingredient?.ingredient_recipe_step, quantity)
      ),
      weight: dotToComma(
        weightStepTransformer(ingredient?.ingredient_recipe_step ?? null)
      ),
    }
  })
}

const familyStepPropsTransformer = (
  t: any,
  step: RecipeStep,
  familyProfile: FamilyProfile
) => {
  return {
    familyProfile,
    stepProps: {
      backgroundColor: getCardBackgroundColorByFamilyProfile(familyProfile),
      tagProps: {
        icon:
          familyProfile !== FamilyProfile.PARENT
            ? {
                icon: Icons.familyStar,
              }
            : undefined,
        text:
          familyProfile === FamilyProfile.PARENT
            ? t('family_profile_for_parent')
            : t('family_profile_for_children'),
        backgroundColor: getTagBackgroundColorByFamilyProfile(familyProfile),
        textColor: getTagTextColorByFamilyProfile(familyProfile),
      },
      text: wysiwygTransformer(step?.text ?? ''),
    },
  }
}

const stepsTransformer = (steps: RecipeStep[], t: any) => {
  return [...(steps ?? [])]
    ?.sort((a: RecipeStep, b: RecipeStep) => a?.order - b?.order)
    .map((step: RecipeStep) => ({
      text: wysiwygTransformer(step?.text ?? ''),
      ...(step?.image
        ? {
            imageProps: {
              src: step?.image ?? '',
              alt: step?.image_alt ?? '',
              objectFit: 'cover',
              layout: 'fill',
            } as ImageProps,
          }
        : {}),
      ...(step?.video?.file
        ? {
            videoProps: {
              watchTranscriptText: t('recipe_steps_transcript_cta'),
              video: {
                poster: step?.video?.poster ?? '',
                src: step?.video?.file ?? '',
                transcription: step?.video?.transcript ?? '',
              },
            } as VideoProps,
          }
        : {}),
      ingredients: stepsIngredientsTransformer(step?.ingredients),
      ...(step?.family_profile && {
        familyStepProps: familyStepPropsTransformer(
          t,
          step,
          step.family_profile as FamilyProfile
        ),
      }),
    }))
}

type RecipeStepsTransformerProps = {
  recipe: Recipe | null
  t: any
  isStepByStepOpen: boolean
  toggleStepByStepHandler: () => void
}

export const recipeStepsDefaultTransformer = (
  args: RecipeStepsTransformerProps
) => {
  const { recipe, t, isStepByStepOpen, toggleStepByStepHandler } = args

  return {
    title: t('recipe_steps_title'),
    fullscreenStepsButtonProps: {
      text: t('recipe_steps_fullscreen_cta'),
      icon: 'expand' as IconName,
      variantType: 'button',
      variantColorScheme: 'colored',
      variantColor: colors.redMojo,
      actionProps: {
        onClick: toggleStepByStepHandler,
        'aria-label': t('recipe_steps_fullscreen_cta'),
      },
    } as ActionButtonProps,
    steps: stepsTransformer(recipe?.steps ?? [], t),
    videoProps: {
      watchTranscriptText: t('recipe_steps_transcript_cta'),
      video: {
        poster: recipe?.video?.poster ?? '',
        src: recipe?.video?.file ?? '',
        transcription: recipe?.video?.transcript ?? '',
      },
    },
    stepByStepProps: {
      isOpen: isStepByStepOpen,
      onClose: toggleStepByStepHandler,
      textClose: t('recipe_steps_fullscreen_close'),
      backgroundImageMobile: {
        alt: 'Stepping background',
        src: '/images/CoverSection/Banner_BG_Mobile_full.png',
        layout: 'fill',
        objectFit: 'cover',
      } as ImageProps,
    },
  }
}

export const recipeStepsForFamilyTransformer = (
  args: RecipeStepsTransformerProps
) => {
  const defaultSteps = recipeStepsDefaultTransformer(args)
  const childrenProfile = getRecipeChildProfile(args.recipe as Recipe)

  const route = Router.getRouteConfig(routes.recipesByTag.name, {
    tag: 'à 4 mains',
  })

  return {
    ...defaultSteps,
    collapsableProps: {
      backgroundColor: colors.pureWhite,
      backgroundImage:
        APP.ASSET_PREFIX + '/images/family/background_desktop_grey.webp',
    },
    customStepRenderer: (step: StepProps, index: number) =>
      (
        <FamilyStep
          key={`step-${index}`}
          step={step.familyStepProps!}
          index={index}
        />
      ) as React.ReactNode,
    highlightTitleProps: {
      text: defaultSteps.title,
      backgroundColor: colors.familyYellow,
      color: colors.familyBurgundy,
    },
    familyStepByStepProps: {
      ...defaultSteps.stepByStepProps,
      steps: defaultSteps.steps,
      highlightTitleProps: {
        text: args.recipe?.name ?? '',
        backgroundColor: colors.familyYellow,
        color: colors.familyBurgundy,
      },
      mainImage: {
        src: args.recipe?.image
          ? args?.recipe?.image
          : // FIXME: Hardcoded
            '/images/default/placeholder.png',
        alt: args.recipe?.image_alt ?? '',
        width: 750,
        height: 500,
        fallbacksrc:
          api.API_URL +
          `/storage/recettes_generiques/${args.t(
            `recipe_type_${args.recipe?.recipe_category}_img_fallback`
          )}`,
      } as ImageProps,

      secondaryImage: {
        alt: 'Secondary image',
        src: getSecondaryBackgroundImageByFamilyProfile(childrenProfile),
        width: 400,
        height: 320,
      } as ImageProps,
      tag: {
        text: 'Les recettes à 4 mains',
        backgroundColor: colors.familyBlue,
        textColor: colors.familyBlue,
        href: route.href,
        nextAs: route.nextAs,
      },
      intro: args.t('family_recipe_intro'),
    },
  }
}

export const recipeStepsTransformer = (args: RecipeStepsTransformerProps) => {
  const { recipe } = args
  if (recipe?.family_recipe) {
    return recipeStepsForFamilyTransformer(args)
  } else {
    return recipeStepsDefaultTransformer(args)
  }
}
